import * as React from "react";
import {
  Box,
  Collapse,
  IconButton,
  LinearProgress,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from "@material-ui/core";
import {
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
} from "@material-ui/icons";

import { formatMoney } from "lib/helpers";
import { Source } from "services/valuation/types";
import theme from "theme";

const useRowStyles = makeStyles({
  root: {
    "& > *": {
      borderBottom: "none",
      padding: "0 5px",
      height: "30px",
    },
  },
  progressBar: {
    height: 7,
    borderRadius: 5,
    backgroundColor: theme.palette.grey[300],
    "& .MuiLinearProgress-bar": {
      backgroundColor: (props: { color: string }) => props.color,
    },
  },
  collapseProgressBar: {
    height: 15,
    paddingLeft: 25,
  },
  collapseCell: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
});

type SourceBreakdownTableRowProps = {
  color: string;
  source: Source;
  totalValue: number;
};

const SourceBreakdownTableRow: React.FC<SourceBreakdownTableRowProps> = ({
  source,
  totalValue,
  color,
}) => {
  const [open, setOpen] = React.useState(true);
  const classes = useRowStyles({ color });

  return (
    <>
      <TableRow className={classes.root}>
        <TableCell>{source.name}</TableCell>
        <TableCell>${formatMoney(source.earnings, 0)}</TableCell>
        <TableCell>
          <Box className={classes.collapseCell}>
            <Box>{Math.round((source.earnings / totalValue) * 100)}%</Box>
            <Box>
              {!!source.earning_details?.length && (
                <IconButton
                  aria-label="expand row"
                  size="small"
                  onClick={() => setOpen(!open)}
                >
                  {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                </IconButton>
              )}
            </Box>
          </Box>
        </TableCell>
      </TableRow>
      <TableRow className={classes.root}>
        <TableCell colSpan={3} style={{ height: "15px" }}>
          <LinearProgress
            variant="determinate"
            value={Math.round((source.earnings / totalValue) * 100)}
            className={classes.progressBar}
          />
        </TableCell>
      </TableRow>
      {!!source.earning_details?.length && open && (
        <TableRow className={classes.root}>
          <TableCell colSpan={6}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Table
                style={{ marginBottom: "10px" }}
                aria-label="earning-details-table"
              >
                {source.earning_details.map((earningDetails: Source, index) => (
                  <TableBody key={`row-${earningDetails.name}-${index}`}>
                    <TableRow className={classes.root}>
                      <TableCell />
                      <TableCell
                        style={{
                          width: "150px",
                        }}
                      >
                        {earningDetails.name}
                      </TableCell>
                      <TableCell
                        style={{
                          width: "60px",
                        }}
                      >
                        ${formatMoney(earningDetails.earnings, 0)}
                      </TableCell>
                      <TableCell>
                        {Math.round(
                          (earningDetails.earnings / source.earnings) * 100,
                        )}
                        %
                      </TableCell>
                    </TableRow>
                    <TableRow className={classes.root}>
                      <TableCell
                        colSpan={4}
                        className={classes.collapseProgressBar}
                      >
                        <LinearProgress
                          variant="determinate"
                          value={Math.round(
                            (earningDetails.earnings / source.earnings) * 100,
                          )}
                          className={classes.progressBar}
                        />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                ))}
              </Table>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

export default SourceBreakdownTableRow;
